import { APIComponentAttributes } from '@ardoq/api-types';
import { GridApi } from 'ag-grid-enterprise';
import { deduplicateRoutes } from './agGridRouteOperations';
import { hierarchyInAgGridOperations } from './hierarchy/hierarchyInAgGridOperations';
import {
  InventoryContext,
  RefreshServerSideOptions,
  RefreshServerSideParams,
} from './types';
import { FilterModel } from './filters/types';
import { inventoryHierarchyExpansionOperations } from './hierarchy/inventoryHierarchyExpansionOperations';

/**
 * The single hierarchy-safe entry point for refreshing the inventory
 * server-side grid.
 *
 * A routeless AG Grid refresh (default `purge: false`) only reloads the root
 * block, so in hierarchy view expanded child rows keep stale data. When no
 * route is given we therefore purge the whole store (which cascades to every
 * nesting level), clearing the hierarchy cache and preserving the expanded
 * tree. A call with an explicit `route` is a targeted refresh of one block and
 * is passed through unchanged.
 *
 * `hierarchyRefreshMode` selects how a routeless refresh behaves in hierarchy
 * view; it has no effect in flat view.
 * - `purgeWithExpansionCapture` (the default when no options are given)
 *   captures the expanded nodes for re-expansion, then purges the store.
 * - `purgeWithoutExpansionCapture` skips that capture, for callers that have
 *   already captured (and possibly marked) the nodes to re-expand and would
 *   have their marks overwritten by it.
 * - `inPlace` refreshes every loaded block individually instead of purging the
 *   whole store. The row nodes survive, so scroll position and expansion state
 *   are kept. Visible blocks — expanded, and with every ancestor expanded too —
 *   are refreshed with `purge: false` so their rows stay in place while the new
 *   data loads. Every other loaded block is refreshed with `purge: true`, which
 *   discards its cached rows without queueing a request, so the next expand
 *   fetches fresh data. A purge cascades to descendant stores, so the purged
 *   routes are deduplicated to the shallowest ancestors; the in-place ones
 *   cannot be, as `purge: false` does not cascade.
 *
 *   Nothing is purged while filters are applied. In that mode children are not
 *   fetched per parent but sliced out of the shared hierarchy cache that
 *   `refreshHierarchy()` has just emptied, and that cache repopulates one page
 *   per request — so a purged block can come back with fewer children than its
 *   count reports. Keeping the block means it renders its retained rows on the
 *   next expand instead.
 *
 * All refreshes should go through this helper (or the InventoryGridApi adapter,
 * which delegates here).
 */
export const refreshServerSideForGrid = (
  gridApi: GridApi<APIComponentAttributes>,
  params?: RefreshServerSideParams,
  options?: RefreshServerSideOptions
): void => {
  if (params?.route) {
    gridApi.refreshServerSide(params);
    return;
  }

  const context = gridApi.getGridOption('context') as
    | InventoryContext
    | undefined;

  if (context?.hierarchy.isHierarchyEnabled) {
    context.commands.refreshHierarchy();

    const hierarchyRefreshMode =
      options?.hierarchyRefreshMode ?? 'purgeWithExpansionCapture';

    if (hierarchyRefreshMode === 'inPlace') {
      const { routesToRefreshInPlace, routesToPurge } =
        hierarchyInAgGridOperations.getRoutesOfLoadedBlocks(gridApi);
      routesToRefreshInPlace.forEach(route =>
        gridApi.refreshServerSide({ route, purge: false })
      );
      if (!hierarchyInAgGridOperations.hasFiltersApplied(gridApi)) {
        deduplicateRoutes(routesToPurge).forEach(route =>
          gridApi.refreshServerSide({ route, purge: true })
        );
      }
      return;
    }

    if (hierarchyRefreshMode === 'purgeWithExpansionCapture') {
      hierarchyInAgGridOperations.captureExpandedNodesForReexpansion(gridApi);
    }
    gridApi.refreshServerSide({ purge: true });
    return;
  }

  // Flat view has no child blocks, so a routeless root refresh is enough.
  gridApi.refreshServerSide({ purge: false });
};

/**
 * "Collapse All": snapshot the current filters (entering collapsed mode) and
 * purge so the grid reloads as a `hierarchyCollapsed` root fetch. Falls back
 * to AG Grid's client-side `collapseAll()` when hierarchy view isn't active.
 */
export const collapseAllForGrid = (
  gridApi: GridApi<APIComponentAttributes>
): void => {
  const context = gridApi.getGridOption('context') as
    | InventoryContext
    | undefined;
  const hierarchy = context?.hierarchy;

  if (hierarchy?.isHierarchyEnabled) {
    hierarchy.nodeExpansion =
      inventoryHierarchyExpansionOperations.getCollapsedAll(
        gridApi.getFilterModel() as FilterModel
      );
    refreshServerSideForGrid(gridApi, undefined, {
      hierarchyRefreshMode: 'purgeWithoutExpansionCapture',
    });
    return;
  }

  gridApi.collapseAll();
};

/**
 * "Expand All": opens every group node the grid has already loaded, and enters
 * expand-all mode so each block that arrives from those expansions opens too.
 * The tree therefore keeps unfolding level by level, one request per parent,
 * until it is fully expanded.
 *
 * Nothing is purged: the loaded rows stay where they are, so the expansion
 * builds on the current view instead of reloading it from the root. Collapsed
 * mode is left, since its root-only `hierarchyCollapsed` fetch is the opposite
 * of what we want next.
 */
export const expandAllForGrid = (
  gridApi: GridApi<APIComponentAttributes>
): void => {
  const context = gridApi.getGridOption('context') as
    | InventoryContext
    | undefined;
  const hierarchy = context?.hierarchy;

  if (hierarchy?.isHierarchyEnabled) {
    hierarchy.nodeExpansion =
      inventoryHierarchyExpansionOperations.getExpandedAll();
  }

  gridApi.expandAll();
};
